/**
 * CTAP2 Commands — PIN Protocol & MakeCredential
 *
 * Implements the CTAP2 authenticator commands needed for FIDO2 pre-provisioning:
 * - authenticatorGetInfo (0x04)
 * - authenticatorClientPIN (0x06) for PIN protocol 1
 * - authenticatorMakeCredential (0x01)
 *
 * Uses PIN Protocol 1 (ECDH P-256 + AES-256-CBC + SHA-256).
 *
 * @see https://fidoalliance.org/specs/fido-v2.1-ps-20210615/fido-client-to-authenticator-protocol-v2.1-ps-errata-20220621.html
 */

import { encode as cborEncode, decode as cborDecode } from 'cbor-x';
import type { Ctap2HidConnection } from './ctap2-hid';
import { ctap2Command } from './ctap2-hid';

// ─── CTAP2 Command Codes ─────────────────────────────────────────────────────

const CMD_MAKE_CREDENTIAL = 0x01;
const CMD_GET_INFO = 0x04;
const CMD_CLIENT_PIN = 0x06;

// ─── PIN Protocol Sub-commands ───────────────────────────────────────────────

const PIN_CMD_GET_KEY_AGREEMENT = 0x02;
const PIN_CMD_SET_PIN = 0x03;
const PIN_CMD_GET_PIN_TOKEN = 0x05;

// ─── PIN Protocol 1 Constants ────────────────────────────────────────────────

const PIN_PROTOCOL_VERSION = 1;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AuthenticatorInfo {
  versions: string[];
  extensions?: string[];
  aaguid: Uint8Array;
  options?: Record<string, boolean>;
  maxMsgSize?: number;
  pinUvAuthProtocols?: number[];
  maxCredentialCountInList?: number;
  maxCredentialIdLength?: number;
  transports?: string[];
  algorithms?: Array<{ type: string; alg: number }>;
  firmwareVersion?: number;
}

export interface MakeCredentialParams {
  clientDataHash: Uint8Array;
  rp: { id: string; name: string };
  user: { id: Uint8Array; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: string; alg: number }>;
  excludeList?: Array<{ type: string; id: Uint8Array }>;
  extensions?: Record<string, unknown>;
  options?: { rk?: boolean; uv?: boolean };
  pinUvAuthParam?: Uint8Array;
  pinUvAuthProtocol?: number;
}

export interface MakeCredentialResult {
  fmt: string;
  authData: Uint8Array;
  attStmt: Record<string, unknown>;
}

// ─── authenticatorGetInfo ────────────────────────────────────────────────────

/**
 * Get information about the authenticator's capabilities.
 */
export async function authenticatorGetInfo(
  conn: Ctap2HidConnection
): Promise<AuthenticatorInfo> {
  const response = await ctap2Command(conn, CMD_GET_INFO);
  const decoded = cborDecode(response) as Map<number, unknown>;

  return {
    versions: (decoded.get(0x01) as string[]) ?? [],
    extensions: decoded.get(0x02) as string[] | undefined,
    aaguid: decoded.get(0x03) as Uint8Array,
    options: cborMapToObject(decoded.get(0x04) as Map<string, boolean> | undefined),
    maxMsgSize: decoded.get(0x05) as number | undefined,
    pinUvAuthProtocols: decoded.get(0x06) as number[] | undefined,
    maxCredentialCountInList: decoded.get(0x07) as number | undefined,
    maxCredentialIdLength: decoded.get(0x08) as number | undefined,
    transports: decoded.get(0x09) as string[] | undefined,
    algorithms: decoded.get(0x0a) as Array<{ type: string; alg: number }> | undefined,
    firmwareVersion: decoded.get(0x0e) as number | undefined,
  };
}

// ─── PIN Management ──────────────────────────────────────────────────────────

/**
 * Get the authenticator's key agreement public key for PIN protocol.
 * Returns the raw COSE_Key as CBOR-encoded data.
 */
async function getPinKeyAgreement(
  conn: Ctap2HidConnection
): Promise<CryptoKey> {
  const params = new Map<number, unknown>();
  params.set(0x01, PIN_PROTOCOL_VERSION);  // pinUvAuthProtocol
  params.set(0x02, PIN_CMD_GET_KEY_AGREEMENT); // subCommand

  const response = await ctap2Command(conn, CMD_CLIENT_PIN, cborEncode(params));
  const decoded = cborDecode(response) as Map<number, unknown>;

  const keyAgreement = decoded.get(0x01) as Map<number, unknown>;
  if (!keyAgreement) {
    throw new Error('No keyAgreement in PIN response');
  }

  return importCoseP256Key(keyAgreement);
}

/**
 * Set a new PIN on the authenticator (when no PIN is currently set).
 * Implements PIN Protocol 1 SET_PIN flow.
 */
export async function setPin(
  conn: Ctap2HidConnection,
  newPin: string
): Promise<void> {
  // Validate PIN length (CTAP2 requires 4-63 bytes in UTF-8)
  const pinBytes = new TextEncoder().encode(newPin);
  if (pinBytes.length < 4 || pinBytes.length > 63) {
    throw new Error('PIN must be 4-63 bytes when UTF-8 encoded');
  }

  // Get authenticator's key agreement public key
  const authPubKey = await getPinKeyAgreement(conn);

  // Generate platform key pair for ECDH
  const platformKeyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits']
  );

  // Derive shared secret via ECDH
  const sharedSecret = await deriveSharedSecret(platformKeyPair.privateKey, authPubKey);

  // Pad PIN to 64 bytes
  const paddedPin = new Uint8Array(64);
  paddedPin.set(pinBytes);

  // Encrypt the new PIN: AES-256-CBC(sharedSecret, IV=0, paddedPin)
  const newPinEnc = await aes256CbcEncrypt(sharedSecret, paddedPin);

  // Compute pinUvAuthParam = LEFT(HMAC-SHA-256(sharedSecret, newPinEnc), 16)
  const pinAuth = await computePinAuth(sharedSecret, newPinEnc);

  // Export platform public key as COSE_Key
  const platformPubKeyCose = await exportCoseP256Key(platformKeyPair.publicKey);

  // Build the command
  const params = new Map<number, unknown>();
  params.set(0x01, PIN_PROTOCOL_VERSION);
  params.set(0x02, PIN_CMD_SET_PIN);
  params.set(0x03, platformPubKeyCose);
  params.set(0x04, pinAuth);
  params.set(0x05, newPinEnc);

  await ctap2Command(conn, CMD_CLIENT_PIN, cborEncode(params));
}

/**
 * Get a PIN token using PIN Protocol 1.
 * The token is used to authenticate subsequent commands (e.g. MakeCredential).
 */
export async function getPinToken(
  conn: Ctap2HidConnection,
  pin: string
): Promise<Uint8Array> {
  // Get authenticator's key agreement public key
  const authPubKey = await getPinKeyAgreement(conn);

  // Generate platform key pair for ECDH
  const platformKeyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits']
  );

  // Derive shared secret
  const sharedSecret = await deriveSharedSecret(platformKeyPair.privateKey, authPubKey);

  // Compute PIN hash: LEFT(SHA-256(PIN), 16)
  const pinBytes = new TextEncoder().encode(pin);
  const pinHashFull = new Uint8Array(await crypto.subtle.digest('SHA-256', pinBytes));
  const pinHashLeft16 = pinHashFull.slice(0, 16);

  // Encrypt PIN hash: AES-256-CBC(sharedSecret, IV=0, pinHashLeft16)
  const pinHashEnc = await aes256CbcEncrypt(sharedSecret, pinHashLeft16);

  // Export platform public key as COSE_Key
  const platformPubKeyCose = await exportCoseP256Key(platformKeyPair.publicKey);

  // Build the command
  const params = new Map<number, unknown>();
  params.set(0x01, PIN_PROTOCOL_VERSION);
  params.set(0x02, PIN_CMD_GET_PIN_TOKEN);
  params.set(0x03, platformPubKeyCose);
  params.set(0x06, pinHashEnc);

  const response = await ctap2Command(conn, CMD_CLIENT_PIN, cborEncode(params));
  const decoded = cborDecode(response) as Map<number, unknown>;

  const pinTokenEnc = decoded.get(0x02) as Uint8Array;
  if (!pinTokenEnc) {
    throw new Error('No pinUvAuthToken in response');
  }

  // Decrypt the PIN token: AES-256-CBC-Decrypt(sharedSecret, IV=0, pinTokenEnc)
  return aes256CbcDecrypt(sharedSecret, pinTokenEnc);
}

// ─── authenticatorMakeCredential ─────────────────────────────────────────────

/**
 * Create a new credential on the authenticator via CTAP2.
 * This is the core operation that bypasses the browser's RP ID restriction.
 */
export async function authenticatorMakeCredential(
  conn: Ctap2HidConnection,
  params: MakeCredentialParams
): Promise<MakeCredentialResult> {
  // Build CBOR parameter map per CTAP2 spec
  const cborParams = new Map<number, unknown>();

  // 0x01: clientDataHash (32 bytes)
  cborParams.set(0x01, params.clientDataHash);

  // 0x02: rp { id, name }
  cborParams.set(0x02, { id: params.rp.id, name: params.rp.name });

  // 0x03: user { id, name, displayName }
  cborParams.set(0x03, {
    id: params.user.id,
    name: params.user.name,
    displayName: params.user.displayName,
  });

  // 0x04: pubKeyCredParams
  cborParams.set(
    0x04,
    params.pubKeyCredParams.map((p) => ({
      type: p.type,
      alg: p.alg,
    }))
  );

  // 0x05: excludeList (optional)
  if (params.excludeList && params.excludeList.length > 0) {
    cborParams.set(
      0x05,
      params.excludeList.map((c) => ({
        type: c.type,
        id: c.id,
      }))
    );
  }

  // 0x06: extensions (optional)
  if (params.extensions && Object.keys(params.extensions).length > 0) {
    cborParams.set(0x06, params.extensions);
  }

  // 0x07: options (optional)
  if (params.options) {
    cborParams.set(0x07, params.options);
  }

  // 0x08: pinUvAuthParam (optional)
  if (params.pinUvAuthParam) {
    cborParams.set(0x08, params.pinUvAuthParam);
  }

  // 0x09: pinUvAuthProtocol (optional)
  if (params.pinUvAuthProtocol !== undefined) {
    cborParams.set(0x09, params.pinUvAuthProtocol);
  }

  const response = await ctap2Command(conn, CMD_MAKE_CREDENTIAL, cborEncode(cborParams));
  const decoded = cborDecode(response) as Map<number, unknown>;

  const fmt = decoded.get(0x01) as string;
  const authData = decoded.get(0x02) as Uint8Array;
  const attStmt = decoded.get(0x03) as Record<string, unknown>;

  if (!fmt || !authData) {
    throw new Error('Invalid MakeCredential response: missing fmt or authData');
  }

  return { fmt, authData, attStmt: attStmt ?? {} };
}

/**
 * Compute the pinUvAuthParam for a MakeCredential command.
 * pinUvAuthParam = HMAC-SHA-256(pinToken, clientDataHash), truncated to 16 bytes.
 */
export async function computeMakeCredentialAuth(
  pinToken: Uint8Array,
  clientDataHash: Uint8Array
): Promise<Uint8Array> {
  return computePinAuth(pinToken, clientDataHash);
}

// ─── Crypto Helpers — PIN Protocol 1 ─────────────────────────────────────────

/**
 * Import a COSE P-256 public key (from authenticator) as a WebCrypto key.
 */
async function importCoseP256Key(
  coseKey: Map<number, unknown>
): Promise<CryptoKey> {
  const x = coseKey.get(-2) as Uint8Array;
  const y = coseKey.get(-3) as Uint8Array;

  if (!x || !y || x.length !== 32 || y.length !== 32) {
    throw new Error('Invalid COSE P-256 key: missing or wrong-size x/y');
  }

  // Build an uncompressed EC point: 0x04 || x || y
  const rawKey = new Uint8Array(65);
  rawKey[0] = 0x04;
  rawKey.set(x, 1);
  rawKey.set(y, 33);

  return crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    []
  );
}

/**
 * Export a WebCrypto P-256 public key as a COSE_Key Map for CTAP2.
 */
async function exportCoseP256Key(
  key: CryptoKey
): Promise<Map<number, unknown>> {
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', key));
  // raw is 65 bytes: 0x04 || x(32) || y(32)
  const x = raw.slice(1, 33);
  const y = raw.slice(33, 65);

  const cose = new Map<number, unknown>();
  cose.set(1, 2);     // kty: EC2
  cose.set(3, -25);   // alg: ECDH-ES+HKDF-256
  cose.set(-1, 1);    // crv: P-256
  cose.set(-2, x);    // x coordinate
  cose.set(-3, y);    // y coordinate

  return cose;
}

/**
 * Derive shared secret: SHA-256(ECDH(platformKey, authKey))
 * Per PIN Protocol 1.
 */
async function deriveSharedSecret(
  platformPrivateKey: CryptoKey,
  authPublicKey: CryptoKey
): Promise<Uint8Array> {
  const ecdhBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: authPublicKey },
    platformPrivateKey,
    256
  );

  const hash = await crypto.subtle.digest('SHA-256', ecdhBits);
  return new Uint8Array(hash);
}

/**
 * AES-256-CBC encrypt with a zero IV.
 * PIN Protocol 1 always uses IV = 16 zero bytes.
 */
async function aes256CbcEncrypt(
  key: Uint8Array,
  data: Uint8Array
): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key.buffer as ArrayBuffer,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  );

  const iv = new Uint8Array(16); // zero IV for PIN Protocol 1
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    cryptoKey,
    data.buffer as ArrayBuffer
  );

  return new Uint8Array(encrypted);
}

/**
 * AES-256-CBC decrypt with a zero IV.
 */
async function aes256CbcDecrypt(
  key: Uint8Array,
  data: Uint8Array
): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key.buffer as ArrayBuffer,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  );

  const iv = new Uint8Array(16); // zero IV for PIN Protocol 1
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    cryptoKey,
    data.buffer as ArrayBuffer
  );

  return new Uint8Array(decrypted);
}

/**
 * Compute pinUvAuthParam: LEFT(HMAC-SHA-256(key, message), 16)
 */
async function computePinAuth(
  key: Uint8Array,
  message: Uint8Array
): Promise<Uint8Array> {
  const hmacKey = await crypto.subtle.importKey(
    'raw',
    key.buffer as ArrayBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', hmacKey, message.buffer as ArrayBuffer);
  return new Uint8Array(signature).slice(0, 16);
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function cborMapToObject(
  map: Map<string, boolean> | undefined
): Record<string, boolean> | undefined {
  if (!map) return undefined;
  const obj: Record<string, boolean> = {};
  for (const [key, value] of map) {
    obj[key] = value;
  }
  return obj;
}
